import {
  EmptyState,
  TriageDetailPanel,
  useToast,
  TriageListPanel,
  TriageSourceImageModal,
  TriageTopBarActions,
  TriageUrgencyBanner,
  applyTriageAction,
  filterTriageItems,
  tiCurrentUser,
  tiItemKind,
  triageHeaderStats,
  type TriageAssigneeSelection,
  type TriageChannelFilter,
  type TriageItem,
  type TriageSegment,
  type TriageStatus,
  type TriageStatusSelection,
  TI_STATUSES,
} from '@elva/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { extractApiErrorMessage } from '@/api/errors';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import { adaptTriageDetail, adaptTriageItems, toApiAssigneeId } from '../adapters/triageAdapters';
import { useCreateTriageNote, useTriageItem } from '../hooks/useTriageItem';
import { useTriageIdentity } from '../hooks/useTriageIdentity';
import { useUpdateTriageAssignee, useUpdateTriageStatus } from '../hooks/useTriageMutations';
import { useTriageList } from '../hooks/useTriageList';
import { useTriageSourceImages } from '../hooks/useTriageSourceImage';

/** `recall` → *Recall*. Unknown sources print as given rather than as a blank. */
function sourceLabel(source: string): string {
  return source.charAt(0).toUpperCase() + source.slice(1).replace(/[-_]/g, ' ');
}

export function TriagePage() {
  useTriageIdentity();
  // `?source=recall` — an agent page linking here lands on ITS queue (amendment
  // 24 §7). Arriving with a source also clears the open-only status filter, so
  // the number in the tab and the number of rows are the same number.
  // The rows themselves stay the triage API's; the source only filters them.
  const [params, setParams] = useSearchParams();
  const source = params.get('source') ?? undefined;
  // Spec 31 §7: the Calls-to-make segment is gone — Triage opens on Attention.
  const [segment, setSegment] = useState<TriageSegment>('attention');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [statusSelection, setStatusSelection] = useState<TriageStatusSelection>(
    source === undefined ? ['open'] : [],
  );
  const [channelFilter, setChannelFilter] = useState<TriageChannelFilter>('All');
  const [assigneeSelection, setAssigneeSelection] = useState<TriageAssigneeSelection>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [noteText, setNoteText] = useState('');

  const { toast } = useToast();
  const { data: listData, isLoading, isError } = useTriageList();
  const uuidByIdRef = useRef(new Map<number, string>());
  const idByUuidRef = useRef(new Map<string, number>());

  const serverItems = useMemo(() => {
    const adapted = adaptTriageItems(listData?.items ?? [], idByUuidRef.current);
    uuidByIdRef.current = adapted.uuidById;
    return adapted.items;
  }, [listData?.items]);

  const [items, setItems] = useState<TriageItem[]>([]);
  useEffect(() => {
    setItems(serverItems);
  }, [serverItems]);

  useEffect(() => {
    const inScope = (i: TriageItem) => source === undefined || i.triggeredBy === source;
    if (serverItems.length > 0 && (selectedId == null || !serverItems.some((i) => i.id === selectedId && inScope(i)))) {
      const first =
        serverItems.find((i) => tiItemKind(i) === segment && inScope(i) && i.status === 'open') ??
        serverItems.find((i) => tiItemKind(i) === segment && inScope(i));
      setSelectedId(first?.id ?? null);
    }
  }, [serverItems, segment, selectedId, source]);

  useEffect(() => {
    if (isError) console.error('Failed to load triage list');
  }, [isError]);

  const updateStatus = useUpdateTriageStatus();
  const updateAssignee = useUpdateTriageAssignee();

  const selectedUuid = selectedId != null ? uuidByIdRef.current.get(selectedId) : undefined;
  const { data: detail } = useTriageItem(selectedUuid);

  const createNote = useCreateTriageNote(selectedUuid ?? '');
  const queryClient = useQueryClient();

  const filtered = useMemo(
    () =>
      filterTriageItems({
        items,
        segment,
        source,
        statusSelection,
        channelFilter,
        assigneeSelection,
        searchQuery,
      }),
    [items, segment, source, statusSelection, channelFilter, assigneeSelection, searchQuery],
  );

  const segItems = useMemo(
    () =>
      items.filter(
        (i) => tiItemKind(i) === segment && (source === undefined || i.triggeredBy === source),
      ),
    [items, segment, source],
  );

  const rawSelected = items.find((i) => i.id === selectedId);
  const selected = useMemo(() => {
    if (!rawSelected || tiItemKind(rawSelected) !== segment) return null;
    return detail ? { ...rawSelected, ...adaptTriageDetail(detail) } : rawSelected;
  }, [rawSelected, segment, detail]);

  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const sourceImages = useTriageSourceImages(selected?.sourceImages, sourceModalOpen);

  const counts = useMemo(() => {
    const status: Partial<Record<TriageStatus, number>> = {};
    segItems.forEach((i) => {
      status[i.status] = (status[i.status] ?? 0) + 1;
    });
    const channel: Partial<Record<string, number>> = {};
    segItems
      .filter((i) => i.status !== 'closed')
      .forEach((i) => {
        channel[i.channel] = (channel[i.channel] ?? 0) + 1;
      });
    const me = tiCurrentUser().id;
    return {
      status,
      channel,
      mine: segItems.filter((i) => i.assigneeId === me && i.status !== 'closed').length,
      unassigned: segItems.filter((i) => i.assigneeId === null && i.status !== 'closed').length,
    };
  }, [segItems]);

  // With a source on the URL the tab counts are that source's, so the tab and
  // the rows never show two different sevens.
  const scoped = useMemo(
    () => (source === undefined ? items : items.filter((i) => i.triggeredBy === source)),
    [items, source],
  );
  const headerStats = triageHeaderStats(scoped);
  const attentionCount = headerStats.attention;
  const urgentCount = segItems.filter(
    (i) => i.status === 'open' && i.priority === 'urgent',
  ).length;

  const handleSetSegment = useCallback(
    (seg: TriageSegment) => {
      setSegment(seg);
      const inScope = (i: TriageItem) => source === undefined || i.triggeredBy === source;
      const first =
        items.find((i) => tiItemKind(i) === seg && inScope(i) && i.status === 'open') ??
        items.find((i) => tiItemKind(i) === seg && inScope(i));
      setSelectedId(first ? first.id : null);
    },
    [items, source],
  );

  const patchLocalItem = useCallback((id: number, patch: Partial<TriageItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  const handleChangeStatus = useCallback(
    (id: number, nextStatus: TriageStatus) => {
      const current = items.find((i) => i.id === id);
      const uuid = uuidByIdRef.current.get(id);
      if (!current || !uuid) return;
      if (!TI_STATUSES[current.status]?.canTransitionTo.includes(nextStatus)) return;

      patchLocalItem(id, { status: nextStatus });
      updateStatus.mutateAsync({ uuid, status: nextStatus }).catch((err: unknown) => {
        patchLocalItem(id, { status: current.status });
        toast({
          title: 'Couldn’t update status',
          description: extractApiErrorMessage(err),
          variant: 'error',
        });
      });
    },
    [items, patchLocalItem, updateStatus, toast],
  );

  const handleChangeAssignee = useCallback(
    (id: number, nextAssigneeId: string | null) => {
      const current = items.find((i) => i.id === id);
      const uuid = uuidByIdRef.current.get(id);
      if (!current || !uuid) return;

      const apiAssigneeId = toApiAssigneeId(nextAssigneeId);
      if (apiAssigneeId === undefined) {
        toast({
          title: 'Can’t assign yet',
          description: 'Staff list is still loading — try again in a moment.',
          variant: 'error',
        });
        return;
      }

      patchLocalItem(id, { assigneeId: nextAssigneeId });
      updateAssignee
        .mutateAsync({ uuid, assigneeId: apiAssigneeId })
        .catch((err: unknown) => {
          patchLocalItem(id, { assigneeId: current.assigneeId });
          toast({
            title: 'Couldn’t update assignee',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        });
    },
    [items, patchLocalItem, updateAssignee, toast],
  );

  const handleAddNote = useCallback(
    (id: number) => {
      const txt = noteText.trim();
      const uuid = uuidByIdRef.current.get(id);
      if (!txt || !uuid || createNote.isPending) return;
      createNote
        .mutateAsync(txt)
        .then(() => setNoteText(''))
        .catch((err: unknown) => {
          toast({
            title: 'Couldn’t add note',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        });
    },
    [noteText, createNote, toast],
  );

  const handleExecuteAction = useCallback(
    (itemId: number, actionId: string, editedBody?: string) => {
      setItems((prev) => applyTriageAction(prev, itemId, actionId, editedBody));
    },
    [],
  );

  const handleJumpToUrgent = useCallback(() => {
    const firstUrgent = items.find(
      (i) =>
        i.status === 'open' &&
        i.priority === 'urgent' &&
        (source === undefined || i.triggeredBy === source),
    );
    if (firstUrgent) {
      setSegment(tiItemKind(firstUrgent));
      setSelectedId(firstUrgent.id);
    }
  }, [items, source]);

  const handleClearFilters = useCallback(() => {
    setStatusSelection(['open']);
    setChannelFilter('All');
    setAssigneeSelection([]);
    setSearchQuery('');
  }, []);

  const segmentIsEmpty = !isLoading && segItems.length === 0;
  const segmentEmptyState = segmentIsEmpty ? (
    <EmptyState
      size="sm"
      variant="box"
      fill
      icon="check-circ"
      title="Nothing needs attention"
      sub="You're all caught up."
    />
  ) : undefined;

  const handleRefresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['triage', 'list'] });
    setSegment('attention');
    setStatusSelection(['open']);
    setChannelFilter('All');
    setAssigneeSelection([]);
    setSearchQuery('');
    setNoteText('');
  }, [queryClient]);

  usePageTopBar({
    actions: <TriageTopBarActions onRefresh={handleRefresh} />,
  });

  return (
    <div className="ti-root v6-root flex min-h-0 flex-1 flex-col overflow-hidden">
      <TriageUrgencyBanner urgentCount={urgentCount} onJump={handleJumpToUrgent} />
      {source === undefined ? null : (
        <div className="flex items-center gap-2 border-b border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-2 text-[12px] text-[var(--ink-2)]">
          <span>
            Showing <b className="font-semibold text-[var(--ink-0)]">{sourceLabel(source)}</b> only —{' '}
            {attentionCount} need attention.
          </span>
          <button
            type="button"
            onClick={() => {
              setParams(new URLSearchParams());
            }}
            className="font-semibold text-[var(--ai-3)] hover:underline"
          >
            Show everything
          </button>
        </div>
      )}
      <ElvaSectionState isLoading={isLoading && items.length === 0} isError={isError}>
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <TriageListPanel
            items={filtered}
            segment={segment}
            onSegmentChange={handleSetSegment}
            attentionCount={attentionCount}
            counts={counts}
            statusSelection={statusSelection}
            onStatusSelectionChange={setStatusSelection}
            channelFilter={channelFilter}
            onChannelFilterChange={setChannelFilter}
            assigneeSelection={assigneeSelection}
            onAssigneeSelectionChange={setAssigneeSelection}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedId={selectedId}
            onSelectId={setSelectedId}
            onClearFilters={handleClearFilters}
            {...(segmentEmptyState ? { emptyState: segmentEmptyState } : {})}
          />
          <TriageDetailPanel
            item={selected}
            noteText={noteText}
            setNoteText={setNoteText}
            onChangeStatus={handleChangeStatus}
            onChangeAssignee={handleChangeAssignee}
            onAddNote={handleAddNote}
            onExecuteAction={handleExecuteAction}
            onOpenSource={() => setSourceModalOpen(true)}
            {...(segmentIsEmpty
              ? {
                  emptyState: (
                    <EmptyState fill className="w-full" icon="check-circ" title="You're all caught up." />
                  ),
                }
              : {})}
          />
        </div>
      </ElvaSectionState>
      <TriageSourceImageModal
        open={sourceModalOpen}
        onClose={() => setSourceModalOpen(false)}
        title={selected?.sourceLabel || 'Kiosk submission'}
        images={sourceImages}
        alt={selected?.subject ?? ''}
      />
    </div>
  );
}
